import { Atom, PrimitiveAtom, useAtomValue } from 'jotai';
import { Point } from 'ol/geom';
import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';
import { Microfence } from '../../API/types/triggers';
import { StreamData, useGeomobyLiveStream } from '../../API/stream';
import { useGeomobyReplayStream } from '../../API/stream-replay';
import { StrongFeatureHolder } from '../../hooks/geomoby/useLiveMapLoader';
import { LIVE_LAST_EVENTS, LIVE_UPDATES_LATEST_TEN } from '../../store/liveEvents';
import { REPLAY_NOW } from '../../store/replayEvents';
import { KNOWN_TOOLS } from '../../store/tools';
import { ACTIVE } from '../../util/constants';
import { AssetEntity } from '../../util/enums';
import { AssetState, LiveStateUpdate } from '../../util/Events/Messages';
import { ExtendedMap } from '../../util/mapUtils';
import { stringifyIdRecord } from '../../util/stringUtils';
import { assetTypeOfEntity } from './commons';
import { typeAndIdToAssetStateKey } from './Helpers';
import { PortableAssetDto } from '../../API/types/metadata';

export const MapDataContext = createContext<{
  liveData?: StreamData;
  getNow: () => Date;
}>({
  liveData: undefined,
  getNow: () => new Date(),
});

export const setMicrofencetoAssets = (
  assets: ExtendedMap<string, AssetState>,
  microfences: Microfence[],
) => {
  microfences.forEach(microfence => {
    const key = stringifyIdRecord(microfence.assetId);
    const asset = assets.get(key);
    if (asset) {
      assets.set(key, {
        ...asset,
        label: microfence ? microfence.name : asset.label, // Because the back-end is producing the wrong microfence labels.
        microfence,
      });
    }
  });
};

export const setActiveMicrofences = (
  assets: ExtendedMap<string, AssetState>,
  microfences: StrongFeatureHolder<Point, Microfence>[],
) => {
  return microfences.map(microfence => {
    const key = stringifyIdRecord(microfence.feature.get('assetId'));
    const active = !!assets.get(key);
    microfence.feature.set(ACTIVE, active);
    return {
      ...microfence,
      data: {
        ...microfence.data,
        active,
      },
    };
  });
};

const setAssetType = (assets: ExtendedMap<string, AssetState>, knownTools: PortableAssetDto[]) => {
  knownTools.forEach(tool => {
    const key = typeAndIdToAssetStateKey(AssetEntity.Tool, tool.id);
    const asset = assets.get(key);
    if (asset && asset.id.beaconId && asset.type === AssetEntity.Beacon) {
      assets.set(key, {
        ...asset,
        type: assetTypeOfEntity(asset.id, !!tool),
        tool,
      });
    }
  });

  assets.forEach(asset => {
    if (asset.lastSensed) {
      const recentSensed = asset.recentSensed?.map(recent => {
        return {
          ...recent,
          sensed: recent.sensed?.map(sense => {
            if (sense.tool) {
              return sense;
            }
            return {
              ...sense,
              tool: knownTools.find(tool => tool.id === sense.ids.id.beaconId)?.tool,
            };
          }),
        };
      });

      assets.set(stringifyIdRecord(asset.id), {
        ...asset,
        lastSensed: {
          ...asset.lastSensed,
          sensed: asset.lastSensed?.sensed
            ? asset.lastSensed.sensed.map(sense => {
                if (sense.tool) {
                  return sense;
                }
                return {
                  ...sense,
                  tool: knownTools.find(tool => tool.id === sense.ids.id.beaconId)?.tool,
                };
              })
            : [],
        },
        recentSensed,
      });
    }
  });
};

export const MapDataProvider = ({
  lastEventsFromPersistorAtom,
  liveUpdatesLatestsAtom,
  children,
}: {
  children: ReactNode;
  lastEventsFromPersistorAtom?: PrimitiveAtom<LiveStateUpdate[]>;
  liveUpdatesLatestsAtom?: Atom<LiveStateUpdate[]>;
}) => {
  const knownTools = useAtomValue(KNOWN_TOOLS);
  const liveData = useGeomobyLiveStream(
    lastEventsFromPersistorAtom ?? LIVE_LAST_EVENTS,
    liveUpdatesLatestsAtom ?? LIVE_UPDATES_LATEST_TEN,
  );
  setAssetType(liveData.state.assets, knownTools);
  const value = useMemo(
    () => ({
      liveData,
      getNow: () => new Date(),
    }),
    [liveData],
  );
  return <MapDataContext.Provider value={value}>{children}</MapDataContext.Provider>;
};

export const MapDataReplayProvider = ({ children }: { children: ReactNode }) => {
  const knownTools = useAtomValue(KNOWN_TOOLS);
  const replayData = useGeomobyReplayStream();
  setAssetType(replayData.state.assets, knownTools);
  const now = useAtomValue(REPLAY_NOW);
  const getNow = useCallback(() => now, [now]);
  const value = useMemo(
    () => ({
      liveData: replayData,
      getNow,
    }),
    [replayData, getNow],
  );
  return <MapDataContext.Provider value={value}>{children}</MapDataContext.Provider>;
};

export const useMapData = () => useContext(MapDataContext);
